import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Trophy, Gamepad2, Gift, Droplets, Users, Activity, AlertTriangle, TrendingUp } from 'lucide-react'
import { api } from '../lib/api'

type Range = 'today' | '7d' | '30d' | 'all'

const RANGE_LABELS: Record<Range, string> = {
  today: 'Today',
  '7d':  'Last 7 days',
  '30d': 'Last 30 days',
  all:   'All time',
}

interface GameRow {
  id: string
  name_en: string
  name_ar: string
  type: string
  play_frequency: string
  active: boolean
  plays: number
  unique_players: number
  wins: number
  win_rate: number | null
  drops_spent: number
  drops_earned_as_prize: number
}

interface Analytics {
  range: Range
  totals: {
    plays: number
    unique_players: number
    wins: number
    win_rate: number | null
    avg_plays_per_player: number
    drops_spent_on_games: number
    drops_won_from_games: number
  }
  drops_economy: { earned: number; spent: number; net: number }
  prize_funnel: {
    total_winning_plays: number
    redeemed: number
    pending_fresh: number
    pending_stale: number
    redemption_rate: number | null
  }
  engagement: {
    buyers: number
    buyers_who_played: number
    play_engagement_rate: number | null
  }
  margin: {
    prize_cost_paid_fils: number
    prize_liability_fils: number
    drops_outstanding: number
    drop_value_fils: number
    drops_liability_fils: number
    revenue_from_players_fils: number
    revenue_from_non_players_fils: number
    player_buyers: number
    non_player_buyers: number
    player_orders: number
    non_player_orders: number
  }
  per_game: GameRow[]
  top_players: Array<{ id: string; full_name: string; phone: string; plays: number; wins: number; drops_spent: number }>
  top_earners: Array<{ id: string; full_name: string; phone: string; earned: number; spent: number }>
}

const formatNum = (n: number) => n.toLocaleString()
const formatJod = (fils: number) =>
  `JOD ${(fils / 1000).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`

export default function GamesAnalytics() {
  const [range, setRange] = useState<Range>('7d')

  const { data, isLoading } = useQuery<Analytics>({
    queryKey: ['games-analytics', range],
    queryFn: () => api.get(`/games-analytics?range=${range}`).then(r => r.data),
  })

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-lg font-semibold text-white">Games Analytics</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Engagement, drops economy, and prize redemption — tune the prize pool with data.
          </p>
        </div>
        <RangeTabs value={range} onChange={setRange} />
      </div>

      {isLoading || !data ? (
        <div className="px-4 py-16 text-center text-gray-600 text-sm">Loading…</div>
      ) : (
        <>
          {/* Topline cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <SummaryTile icon={<Gamepad2 size={16} />} label="Plays" value={formatNum(data.totals.plays)} accent />
            <SummaryTile
              icon={<Users size={16} />}
              label="Unique players"
              value={formatNum(data.totals.unique_players)}
              hint={data.totals.avg_plays_per_player > 0 ? `${data.totals.avg_plays_per_player} avg per player` : undefined}
            />
            <SummaryTile
              icon={<Trophy size={16} />}
              label="Win rate"
              value={data.totals.win_rate != null ? `${data.totals.win_rate.toFixed(1)}%` : '—'}
              hint={`${formatNum(data.totals.wins)} wins`}
            />
            <SummaryTile
              icon={<Activity size={16} />}
              label="Player engagement"
              value={data.engagement.play_engagement_rate != null ? `${data.engagement.play_engagement_rate.toFixed(1)}%` : '—'}
              hint={`${formatNum(data.engagement.buyers_who_played)} of ${formatNum(data.engagement.buyers)} buyers`}
            />
          </div>

          {/* Drops economy + prize funnel */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
            <Section title="Drops economy" icon={<Droplets size={14} className="text-blue-400" />}>
              <div className="grid grid-cols-3 gap-3 px-3 py-3">
                <EconCell label="Earned" value={formatNum(data.drops_economy.earned)} color="text-green-400" />
                <EconCell label="Spent" value={formatNum(data.drops_economy.spent)} color="text-[#C41E3A]" />
                <EconCell
                  label="Net"
                  value={`${data.drops_economy.net >= 0 ? '+' : ''}${formatNum(data.drops_economy.net)}`}
                  color={data.drops_economy.net >= 0 ? 'text-green-400' : 'text-amber-400'}
                />
              </div>
              <p className="px-3 pb-3 text-[11px] text-gray-600 leading-relaxed">
                {data.drops_economy.net >= 0
                  ? 'Faucet wider than drain — drops accumulating in user wallets.'
                  : 'Drain wider than faucet — users spending faster than they earn.'}
              </p>
            </Section>

            <Section title="Prize redemption funnel" icon={<Gift size={14} className="text-amber-400" />}>
              {data.prize_funnel.total_winning_plays === 0 ? (
                <p className="px-3 py-6 text-sm text-gray-600 text-center">No non-drops prize wins this period.</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3 px-3 py-3">
                    <EconCell
                      label="Redemption rate"
                      value={data.prize_funnel.redemption_rate != null ? `${data.prize_funnel.redemption_rate.toFixed(1)}%` : '—'}
                      color={
                        (data.prize_funnel.redemption_rate ?? 0) >= 70 ? 'text-green-400'
                        : (data.prize_funnel.redemption_rate ?? 0) >= 40 ? 'text-amber-400'
                        : 'text-[#C41E3A]'
                      }
                    />
                    <EconCell label="Total wins" value={formatNum(data.prize_funnel.total_winning_plays)} color="text-white" />
                  </div>
                  <div className="px-3 pb-3 space-y-1 text-xs">
                    <FunnelRow icon={<Gift size={11} className="text-green-400" />} label="Redeemed" value={data.prize_funnel.redeemed} />
                    <FunnelRow icon={<Gift size={11} className="text-blue-400" />} label="Pending (< 7 days)" value={data.prize_funnel.pending_fresh} />
                    <FunnelRow
                      icon={<AlertTriangle size={11} className="text-amber-400" />}
                      label="Pending (> 7 days — stale)"
                      value={data.prize_funnel.pending_stale}
                      highlight={data.prize_funnel.pending_stale > 0}
                    />
                  </div>
                </>
              )}
            </Section>
          </div>

          {/* Margin / liability — JOD view of the drops economy. Tells ops
              whether the games program is paying for itself. */}
          {(() => {
            const m = data.margin
            const playerAov = m.player_orders > 0 ? m.revenue_from_players_fils / m.player_orders : 0
            const nonPlayerAov = m.non_player_orders > 0 ? m.revenue_from_non_players_fils / m.non_player_orders : 0
            const aovUplift = nonPlayerAov > 0 ? ((playerAov - nonPlayerAov) / nonPlayerAov) * 100 : null
            const playerRevenue = m.revenue_from_players_fils
            // Net = revenue from game players this period minus the prize
            // cost paid this period. Positive = games are net contributors.
            const netFils = playerRevenue - m.prize_cost_paid_fils
            return (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
                <Section title="Margin (JOD)" icon={<Droplets size={14} className="text-green-400" />}>
                  <div className="grid grid-cols-2 gap-3 px-3 py-3">
                    <EconCell
                      label="Prize cost paid"
                      value={formatJod(m.prize_cost_paid_fils)}
                      color="text-[#C41E3A]"
                    />
                    <EconCell
                      label="Revenue from players"
                      value={formatJod(playerRevenue)}
                      color="text-green-400"
                    />
                    <EconCell
                      label="Net (players − cost)"
                      value={`${netFils >= 0 ? '+' : ''}${formatJod(netFils)}`}
                      color={netFils >= 0 ? 'text-green-400' : 'text-amber-400'}
                    />
                    <EconCell
                      label="AOV uplift vs non-players"
                      value={aovUplift == null ? '—' : `${aovUplift >= 0 ? '+' : ''}${aovUplift.toFixed(1)}%`}
                      color={(aovUplift ?? 0) >= 0 ? 'text-green-400' : 'text-amber-400'}
                    />
                  </div>
                  <p className="px-3 pb-3 text-[11px] text-gray-600 leading-relaxed">
                    Players: {formatJod(playerRevenue)} across {formatNum(m.player_orders)} orders ({formatJod(playerAov)} avg).
                    Non-players: {formatJod(m.revenue_from_non_players_fils)} across {formatNum(m.non_player_orders)} orders ({formatJod(nonPlayerAov)} avg).
                    Prize cost excludes percentage-discount prizes (need order context) and drops prizes (no JOD cost).
                  </p>
                </Section>
                <Section title="Liability outstanding" icon={<AlertTriangle size={14} className="text-amber-400" />}>
                  <div className="grid grid-cols-2 gap-3 px-3 py-3">
                    <EconCell
                      label="Unredeemed prizes"
                      value={formatJod(m.prize_liability_fils)}
                      color="text-amber-400"
                    />
                    <EconCell
                      label="Drops in wallets (JOD)"
                      value={formatJod(m.drops_liability_fils)}
                      color="text-amber-400"
                    />
                    <EconCell
                      label="Drops outstanding"
                      value={formatNum(m.drops_outstanding)}
                      color="text-blue-400"
                    />
                    <EconCell
                      label="Total liability"
                      value={formatJod(m.prize_liability_fils + m.drops_liability_fils)}
                      color="text-white"
                    />
                  </div>
                  <p className="px-3 pb-3 text-[11px] text-gray-600 leading-relaxed">
                    Drops valued at {formatJod(m.drop_value_fils)} each — tune via{' '}
                    <code className="text-gray-500">store_settings.drop_value_fils</code>.
                    Total = what we'd owe if every pending prize was redeemed AND every drop was spent at face value.
                  </p>
                </Section>
              </div>
            )
          })()}

          {/* Per-game breakdown */}
          <Section title="Per-game breakdown" icon={<Gamepad2 size={14} className="text-purple-400" />}>
            {data.per_game.length === 0 ? (
              <p className="px-3 py-6 text-sm text-gray-600 text-center">No games configured.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-600 border-b border-[#2e2e2e]">
                    <th className="px-3 py-2 text-left font-medium">Game</th>
                    <th className="px-3 py-2 text-right font-medium">Plays</th>
                    <th className="px-3 py-2 text-right font-medium">Players</th>
                    <th className="px-3 py-2 text-right font-medium">Win rate</th>
                    <th className="px-3 py-2 text-right font-medium">Drops spent</th>
                    <th className="px-3 py-2 text-right font-medium">Drops awarded</th>
                  </tr>
                </thead>
                <tbody>
                  {data.per_game.map(g => (
                    <tr key={g.id} className={`border-b border-[#222] ${!g.active ? 'opacity-50' : ''}`}>
                      <td className="px-3 py-2.5">
                        <div className="text-white">{g.name_en}</div>
                        <div className="text-[10px] text-gray-600 capitalize">
                          {g.type.replace(/_/g, ' ')} · {g.play_frequency}
                          {!g.active && ' · inactive'}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right text-white tabular-nums">{formatNum(g.plays)}</td>
                      <td className="px-3 py-2.5 text-right text-gray-400 tabular-nums">{formatNum(g.unique_players)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">
                        {g.win_rate != null ? (
                          <span className={
                            g.win_rate >= 30 ? 'text-amber-400' :
                            g.win_rate >= 10 ? 'text-white'     :
                                              'text-gray-500'
                          }>
                            {g.win_rate.toFixed(1)}%
                          </span>
                        ) : (
                          <span className="text-gray-600">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right text-[#C41E3A] tabular-nums">{formatNum(g.drops_spent)}</td>
                      <td className="px-3 py-2.5 text-right text-green-400 tabular-nums">{formatNum(g.drops_earned_as_prize)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          {/* Top players + top earners side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            <Section title="Top players (by plays)" icon={<Trophy size={14} className="text-amber-400" />}>
              {data.top_players.length === 0 ? (
                <p className="px-3 py-6 text-sm text-gray-600 text-center">No plays yet.</p>
              ) : (
                <div className="divide-y divide-[#222]">
                  {data.top_players.map((p, i) => (
                    <div key={p.id} className="flex items-center gap-3 px-3 py-2.5">
                      <span className={`w-6 text-xs text-center tabular-nums ${i === 0 ? 'text-amber-400 font-bold' : 'text-gray-500'}`}>
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{p.full_name}</p>
                        <p className="text-[11px] text-gray-500">
                          {p.plays} plays · {p.wins} wins · {formatNum(p.drops_spent)} 💧 spent
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section title="Top drops earners" icon={<TrendingUp size={14} className="text-green-400" />}>
              {data.top_earners.length === 0 ? (
                <p className="px-3 py-6 text-sm text-gray-600 text-center">No drops activity.</p>
              ) : (
                <div className="divide-y divide-[#222]">
                  {data.top_earners.map((p, i) => (
                    <div key={p.id} className="flex items-center gap-3 px-3 py-2.5">
                      <span className={`w-6 text-xs text-center tabular-nums ${i === 0 ? 'text-amber-400 font-bold' : 'text-gray-500'}`}>
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{p.full_name}</p>
                        <p className="text-[11px] text-gray-500">
                          +{formatNum(p.earned)} earned · −{formatNum(p.spent)} spent
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>
        </>
      )}
    </div>
  )
}

function RangeTabs({ value, onChange }: { value: Range; onChange: (r: Range) => void }) {
  return (
    <div className="inline-flex bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg p-0.5">
      {(['today', '7d', '30d', 'all'] as Range[]).map(r => (
        <button
          key={r}
          onClick={() => onChange(r)}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            value === r ? 'bg-[#C41E3A] text-white' : 'text-gray-400 hover:text-white'
          }`}
        >
          {RANGE_LABELS[r]}
        </button>
      ))}
    </div>
  )
}

function SummaryTile({ icon, label, value, hint, accent }: {
  icon: React.ReactNode; label: string; value: string; hint?: string; accent?: boolean
}) {
  return (
    <div className={`rounded-lg p-4 border ${
      accent ? 'bg-[#C41E3A]/10 border-[#C41E3A]/30' : 'bg-[#1a1a1a] border-[#2e2e2e]'
    }`}>
      <div className="flex items-center gap-2 mb-1.5 text-gray-500">
        {icon}
        <span className="text-xs">{label}</span>
      </div>
      <p className={`text-xl font-bold tabular-nums ${accent ? 'text-[#C41E3A]' : 'text-white'}`}>
        {value}
      </p>
      {hint && <p className="text-[11px] text-gray-600 mt-0.5">{hint}</p>}
    </div>
  )
}

function Section({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[#2e2e2e]">
        {icon}
        <h2 className="text-sm font-semibold text-white">{title}</h2>
      </div>
      {children}
    </div>
  )
}

function EconCell({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div>
      <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-1">{label}</p>
      <p className={`text-lg font-bold tabular-nums ${color}`}>{value}</p>
    </div>
  )
}

function FunnelRow({ icon, label, value, highlight }: {
  icon: React.ReactNode; label: string; value: number; highlight?: boolean
}) {
  return (
    <div className={`flex items-center justify-between py-1 ${highlight ? 'text-amber-400' : 'text-gray-400'}`}>
      <span className="flex items-center gap-1.5">{icon}{label}</span>
      <span className="tabular-nums font-semibold">{formatNum(value)}</span>
    </div>
  )
}
